import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUser, clearCredentials } from '@/redux/slices/authSlice';
import { clearPlan } from '@/redux/slices/planSlice';
import { authService } from '@/services/auth.service';
import { autoInitPosShiftStore } from '@/utils/posShift';
import type { AppDispatch, RootState } from '@/redux';

const ACCESS_TOKEN_KEY = 'erp_access_token';
const REFRESH_TOKEN_KEY = 'erp_refresh_token';

/**
 * Persisted user thiếu permissions — cần hydrate từ BE.
 * Xảy ra khi:
 *  - Persist state cũ (trước khi thêm permissions field)
 *  - BE chưa trả permissions trong login/me response
 */
function needsPermissionsHydrate(user: unknown): boolean {
  if (!user || typeof user !== 'object') return false;
  const perms = (user as Record<string, unknown>).permissions;
  return !Array.isArray(perms) || perms.length === 0;
}

/**
 * App boot hook — đảm bảo Redux auth state đồng bộ với token trong localStorage.
 *
 * Chạy 1 lần khi App mount:
 *  - Có token trong localStorage + user từ persist thiếu permissions → call `/auth/me`
 *  - Có token + không user → call `/auth/me`
 *  - Không token → no-op.
 *
 * Trả `bootstrapping=true` trong khi chờ /auth/me để App hiện spinner thay vì
 * flash login → home.
 */
export function useAuthBootstrap(): { bootstrapping: boolean } {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((s: RootState) => s.auth.user);
  const accessToken = useSelector((s: RootState) => s.auth.accessToken);
  const [bootstrapping, setBootstrapping] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const tokenInStorage = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!tokenInStorage) return false;
    // Cần hydrate nếu: chưa có user, hoặc user thiếu permissions
    return !user || needsPermissionsHydrate(user);
  });

  useEffect(() => {
    let cancelled = false;
    const tokenInStorage =
      typeof window !== 'undefined' ? localStorage.getItem(ACCESS_TOKEN_KEY) : null;
    const hasToken = !!(accessToken || tokenInStorage);

    if (!hasToken) {
      setBootstrapping(false);
      return;
    }

    // Nếu user đã có permissions đầy đủ → skip hydrate
    if (user && !needsPermissionsHydrate(user)) {
      autoInitPosShiftStore(user);
      setBootstrapping(false);
      return;
    }

    setBootstrapping(true);
    authService
      .me()
      .then((u) => {
        if (cancelled) return;
        dispatch(setUser(u));
        autoInitPosShiftStore(u);
      })
      .catch(() => {
        if (cancelled) return;
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        dispatch(clearCredentials());
        dispatch(clearPlan());
      })
      .finally(() => {
        if (!cancelled) setBootstrapping(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, user, dispatch]);

  return { bootstrapping };
}
